import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../widgets/Icon.tsx";
import { InitDataContext } from "../../context/InitDataContext";
import { useMsal } from "@azure/msal-react";
import { Button } from "primereact/button";
import AvatarImg from "/src/assets/images/male-avatar.png";
import BannerMenuImg from "/src/assets/images/logo_salmones.png";
import { MenuGroup, MenuLink, construirMenu, rutaDe } from "./menuTree.ts";
import "./menu.css";

interface SideMenuProps {
  isOpenMenu: boolean;
  setIsOpenMenu: React.Dispatch<React.SetStateAction<boolean>>;
}

// Accordion groups the user left open (per browser; optional)
const CLAVE_GRUPOS = "fishweb-menu-grupos";

const leerGruposAbiertos = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_GRUPOS) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
};

const guardarGruposAbiertos = (grupos: string[]) => {
  try {
    localStorage.setItem(CLAVE_GRUPOS, JSON.stringify(grupos));
  } catch {
    /* storage not available: the menu still works */
  }
};

const esMovil = () => window.innerWidth <= 768;

function IconoMenu({
  icono,
  iconoBd,
}: {
  icono: string | null;
  iconoBd: string;
}) {
  return icono ? (
    <i className={icono} aria-hidden="true" />
  ) : (
    <Icon icon={(iconoBd || "circle") as any} style={{ fontSize: "1rem" }} />
  );
}

function SideMenu({ isOpenMenu, setIsOpenMenu }: SideMenuProps) {
  const { initData, menus } = useContext(InitDataContext);
  const { instance } = useMsal();
  const location = useLocation();

  const userDetailsAdString = sessionStorage.getItem("user-details");
  const userDetailsAd = userDetailsAdString
    ? JSON.parse(userDetailsAdString)
    : null;

  const arbol = useMemo(() => construirMenu(menus), [menus]);
  const todosLosGrupos = useMemo(
    () => [arbol.reportes, ...arbol.grupos].filter(Boolean) as MenuGroup[],
    [arbol],
  );

  const esActivo = (l: MenuLink) => location.pathname === rutaDe(l.url);
  const grupoActivo = todosLosGrupos.find((g) => g.links.some(esActivo));

  const [abiertos, setAbiertos] = useState<string[]>(leerGruposAbiertos);

  // The group of the current page is always open
  useEffect(() => {
    if (grupoActivo && !abiertos.includes(grupoActivo.key)) {
      setAbiertos((prev) => [...prev, grupoActivo.key]);
    }
  }, [grupoActivo?.key]);

  useEffect(() => {
    guardarGruposAbiertos(abiertos);
  }, [abiertos]);

  // 1. AUTO-COLLAPSE ON ANY ROUTE CHANGE (Mobile Viewport)
  useEffect(() => {
    if (esMovil() && isOpenMenu) {
      setIsOpenMenu(false);
    }
  }, [location.pathname]);

  // 2. HANDLER FOR LINK CLICKS: always collapse on mobile
  const handleNavClick = () => {
    if (esMovil()) {
      setIsOpenMenu(false);
    }
  };

  const alternarGrupo = (key: string) => {
    // Collapsed rail: open the menu with that group visible
    if (!isOpenMenu) {
      setIsOpenMenu(true);
      setAbiertos((prev) => (prev.includes(key) ? prev : [...prev, key]));
      return;
    }
    setAbiertos((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    instance.logoutRedirect().catch((e) => console.error(e));
  };

  const nombreCompleto = initData?.id
    ? `${initData.nombres} ${initData.apellido_paterno} ${initData.apellido_materno || ""}`.trim()
    : userDetailsAd?.name || "Usuario Sin Acceso";

  const iniciales =
    nombreCompleto
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p: string) => p[0]?.toUpperCase())
      .join("") || "U";

  const renderLink = (l: MenuLink, anidado: boolean) => (
    <Link
      key={l.key}
      to={l.url}
      className={`nav-item-link${anidado ? " nav-item-child" : ""}${
        esActivo(l) ? " active-link" : ""
      }${
        // Tour step of the new solicitud screen points here
        l.codigo === "SOCMAN" ? " menu-bar-sub-option-link" : ""
      }`}
      title={isOpenMenu ? undefined : l.nombre}
      aria-current={esActivo(l) ? "page" : undefined}
      onClick={handleNavClick}
    >
      <span className="nav-item-icon-wrapper">
        <IconoMenu icono={l.icono} iconoBd={l.iconoBd} />
      </span>
      {isOpenMenu && <span className="nav-item-label">{l.nombre}</span>}
    </Link>
  );

  const renderGrupo = (g: MenuGroup, destacado = false) => {
    const abierto = isOpenMenu && abiertos.includes(g.key);
    const activo = grupoActivo?.key === g.key;
    const idPanel = `menu-panel-${g.key}`;
    return (
      <div
        key={g.key}
        className={`nav-section${abierto ? " open" : ""}${
          activo ? " has-active" : ""
        }${destacado ? " nav-section-reportes" : ""}`}
      >
        <button
          type="button"
          className="nav-section-header"
          aria-expanded={abierto}
          aria-controls={idPanel}
          title={isOpenMenu ? undefined : g.nombre}
          onClick={() => alternarGrupo(g.key)}
        >
          <span className="nav-item-icon-wrapper">
            <IconoMenu icono={g.icono} iconoBd={g.iconoBd} />
          </span>
          {isOpenMenu && (
            <>
              <span className="nav-item-label">{g.nombre}</span>
              <i
                className="pi pi-chevron-down nav-section-chevron"
                aria-hidden="true"
              />
            </>
          )}
        </button>
        {isOpenMenu && (
          <div id={idPanel} className="nav-section-panel" role="group">
            <div className="nav-section-panel-inner">
              {g.links.map((l) => renderLink(l, true))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`app-sidebar ${isOpenMenu ? "expanded" : "collapsed"}`}
      aria-label="Menú principal"
    >
      {/* Sección Superior: Marca, Botón Toggle y Perfil */}
      <div className="sidebar-top-container">
        <div className="sidebar-header">
          {/* Hidden on the collapsed desktop rail (CSS); the mobile top
              bar shows it */}
          <div className="brand-logo-container">
            <img
              alt="Salmones Logo"
              className="brand-logo"
              src={BannerMenuImg}
            />
          </div>

          <Button
            id="toggleMenuButton"
            type="button"
            className="sidebar-toggle-btn"
            icon={
              isOpenMenu && !esMovil()
                ? "pi pi-angle-double-left"
                : isOpenMenu
                  ? "pi pi-times"
                  : "pi pi-bars"
            }
            text
            onClick={() => setIsOpenMenu(!isOpenMenu)}
            aria-label={isOpenMenu ? "Contraer menú" : "Expandir menú"}
            aria-expanded={isOpenMenu}
          />
        </div>

        {/* Bloque de Perfil de Usuario */}
        <div className="user-profile-section">
          <div className="profile-badge-row" title={nombreCompleto}>
            <span className="profile-avatar-wrap">
              <img
                alt=""
                className="profile-avatar"
                src={AvatarImg}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
              <span className="profile-initials" aria-hidden="true">
                {iniciales}
              </span>
              {initData?.id ? <span className="profile-online-dot" /> : null}
            </span>
            {isOpenMenu && (
              <div className="profile-identity">
                <h4 className="profile-name" title={nombreCompleto}>
                  {nombreCompleto}
                </h4>

                {initData?.id ? (
                  <span className="profile-status-pill">Conectado</span>
                ) : (
                  <span className="profile-status-danger">
                    No tienes acceso al sistema
                  </span>
                )}
              </div>
            )}
          </div>

          {isOpenMenu && initData?.id && (
            <div className="profile-metadata-drawer">
              <div className="meta-item">
                <span className="meta-label">CC</span>
                <span className="meta-value" title={initData.centro_costo}>
                  {initData.centro_costo || "N/A"}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Área</span>
                <span className="meta-value" title={initData.area_nombre}>
                  {initData.area_nombre || "N/A"}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Sucursal</span>
                <span className="meta-value" title={initData.sucursal_nombre}>
                  {initData.sucursal_nombre || "N/A"}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Empresa</span>
                <span className="meta-value" title={initData.empresa_nombre}>
                  {initData.empresa_nombre || "N/A"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navegación: Inicio, Reportes y un acordeón por módulo */}
      <nav className="sidebar-nav-container">
        {initData?.id && menus && menus.length > 0 ? (
          <div className="nav-group">
            {arbol.inicio ? renderLink(arbol.inicio, false) : null}
            {arbol.sueltos.map((l) => renderLink(l, false))}

            {arbol.reportes ? (
              <>
                {isOpenMenu && <span className="nav-caption">Análisis</span>}
                {renderGrupo(arbol.reportes, true)}
              </>
            ) : null}

            {arbol.grupos.length ? (
              <>
                {isOpenMenu && <span className="nav-caption">Gestión</span>}
                {arbol.grupos.map((g) => renderGrupo(g))}
              </>
            ) : null}
          </div>
        ) : null}
      </nav>

      {/* Sección Inferior: Cierre de sesión */}
      <div className="sidebar-bottom-container">
        {initData?.id ? (
          arbol.tieneSalir ? (
            <button
              type="button"
              className="nav-item-link exit-action-link"
              title={isOpenMenu ? undefined : "Cerrar sesión"}
              onClick={logout}
            >
              <span className="nav-item-icon-wrapper">
                <i className="pi pi-sign-out" aria-hidden="true" />
              </span>
              {isOpenMenu && (
                <span className="nav-item-label">Cerrar sesión</span>
              )}
            </button>
          ) : null
        ) : (
          <Button
            icon="pi pi-microsoft"
            label={isOpenMenu ? "Cerrar Sesión AD" : ""}
            className="p-button-danger w-full secondary-exit-btn"
            onClick={logout}
          />
        )}
      </div>
    </aside>
  );
}

export default SideMenu;
